import { useEffect, useState } from 'react'
import type { RealtimeEnvelope, RealtimeEventMap, RealtimeEventName } from '@outfalak/shared'
import { realtime, type RealtimeStatus } from '../lib/realtime.js'

/**
 * Subscribe a component to one realtime event.
 *
 * The handler is held in a ref rather than listed as a dependency, so a screen
 * that closes over changing state does not tear down and re-establish its
 * subscription on every render — which, on a busy store, is how you miss the
 * event you were rendering for.
 */
export function useRealtimeEvent<E extends RealtimeEventName>(
  channel: string | null,
  event: E,
  handler: (data: RealtimeEventMap[E], envelope: RealtimeEnvelope<E>) => void,
): void {
  const [ref] = useState(() => ({ current: handler }))
  ref.current = handler

  useEffect(() => {
    if (!channel) return undefined
    return realtime.onEvent(channel, event, (envelope) => ref.current(envelope.data, envelope))
  }, [channel, event, ref])
}

/** The connection indicator in the header reads this. */
export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(realtime.status)
  useEffect(() => realtime.onStatus(setStatus), [])
  return status
}
